/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  OrganizationEntry,
  OrganizationEntry$inboundSchema,
} from "./organizationentry.js";

export type Trial = {
  endsAt: Date;
  startedAt: Date;
};

export type InfoResponseBody = {
  /**
   * Display-only signup offer variant for an accepted promotion; not billing eligibility
   */
  acceptedSignupOfferFlow?: string | undefined;
  /**
   * Dashboard base URL of the platform host the active organization lives on. Set only for an ordinary session whose request arrived on a different platform host; the dashboard moves there.
   */
  activeOrganizationDashboardUrl?: string | undefined;
  activeOrganizationId: string;
  gramAccountType: string;
  /**
   * Whether the organization has an active billing subscription
   */
  hasActiveSubscription: boolean;
  /**
   * The WorkOS Dashboard operator who initiated this impersonation session. Empty for ordinary authentication.
   */
  impersonatorEmail?: string | undefined;
  isAdmin: boolean;
  /**
   * Whether this is a validated, time-bounded organization support session.
   */
  organizationOverride: boolean;
  /**
   * Fixed expiration of the organization support session.
   */
  organizationOverrideExpiresAt?: Date | undefined;
  organizations: Array<OrganizationEntry>;
  trial: Trial | null;
  userDisplayName?: string | undefined;
  userEmail: string;
  userId: string;
  userPhotoUrl?: string | undefined;
  userSignature?: string | undefined;
  /**
   * Whether the organization is whitelisted to access the platform
   */
  whitelisted: boolean;
};

/** @internal */
export const Trial$inboundSchema: z.ZodMiniType<Trial, unknown> = z.pipe(
  z.object({
    ends_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    started_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "ends_at": "endsAt",
      "started_at": "startedAt",
    });
  }),
);

export function trialFromJSON(
  jsonString: string,
): SafeParseResult<Trial, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Trial$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Trial' from JSON`,
  );
}

/** @internal */
export const InfoResponseBody$inboundSchema: z.ZodMiniType<
  InfoResponseBody,
  unknown
> = z.pipe(
  z.object({
    accepted_signup_offer_flow: z.optional(z.string()),
    active_organization_dashboard_url: z.optional(z.string()),
    active_organization_id: z.string(),
    gram_account_type: z.string(),
    has_active_subscription: z.boolean(),
    impersonator_email: z.optional(z.string()),
    is_admin: z.boolean(),
    organization_override: z.boolean(),
    organization_override_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    organizations: z.array(OrganizationEntry$inboundSchema),
    trial: z.nullable(z.lazy(() => Trial$inboundSchema)),
    user_display_name: z.optional(z.string()),
    user_email: z.string(),
    user_id: z.string(),
    user_photo_url: z.optional(z.string()),
    user_signature: z.optional(z.string()),
    whitelisted: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "accepted_signup_offer_flow": "acceptedSignupOfferFlow",
      "active_organization_dashboard_url": "activeOrganizationDashboardUrl",
      "active_organization_id": "activeOrganizationId",
      "gram_account_type": "gramAccountType",
      "has_active_subscription": "hasActiveSubscription",
      "impersonator_email": "impersonatorEmail",
      "is_admin": "isAdmin",
      "organization_override": "organizationOverride",
      "organization_override_expires_at": "organizationOverrideExpiresAt",
      "user_display_name": "userDisplayName",
      "user_email": "userEmail",
      "user_id": "userId",
      "user_photo_url": "userPhotoUrl",
      "user_signature": "userSignature",
    });
  }),
);

export function infoResponseBodyFromJSON(
  jsonString: string,
): SafeParseResult<InfoResponseBody, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => InfoResponseBody$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'InfoResponseBody' from JSON`,
  );
}
